import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Compass, LogOut, Save, Loader2, Lock, ArrowLeft, MapPin, Plus, Trash2, Pencil, X,
  Bell, Mail, Phone, Building2,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { API, getRegions, getRegion } from "../lib/adventureApi";
import { login, isAdmin, clearToken, authHeader, fetchMe } from "../lib/auth";
import { MONTHS } from "../lib/adventureConstants";
import TripGroupsAdmin from "../components/ra/TripGroupsAdmin";
import MPNPartnersAdmin from "../components/ra/MPNPartnersAdmin";
import CampaignCentreAdmin from "../components/ra/CampaignCentreAdmin";
import CommissionsAdmin from "../components/ra/CommissionsAdmin";
import CurrencyAdmin from "../components/ra/CurrencyAdmin";
import TourMediaAdmin from "../components/ra/TourMediaAdmin";
import RecentAdventureAdmin from "../components/ra/RecentAdventureAdmin";
import WeTravelAdmin from "../components/ra/WeTravelAdmin";
import CallingCardAdmin from "../components/ra/CallingCardAdmin";
import VideoReviewsAdmin from "../components/ra/VideoReviewsAdmin";
import MediaRecoveryAdmin from "../components/ra/MediaRecoveryAdmin";
import VoiceAdmin from "../components/ra/VoiceAdmin";

const TYPES = ["trekking", "culture", "heritage", "wildlife", "wellness", "adventure", "accommodation", "transport", "nature"];
const DIFFICULTIES = ["Easy", "Moderate", "Strenuous"];
const FITNESS = ["None", "Low", "Medium", "High"];

function LoginScreen({ onDone }) {
  const [email, setEmail] = useState("owner@wayfinder.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();
  const submit = async () => {
    setErr(""); setLoading(true);
    try { await login(email, password); onDone(); }
    catch (e) { setErr(e.message || "Login failed"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-base grain flex items-center justify-center p-6" data-testid="admin-login">
      <div className="w-full max-w-sm bg-white border border-sand-dark rounded-md p-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-9 h-9 bg-terracotta rounded-sm flex items-center justify-center"><Lock className="w-5 h-5 text-white" /></div>
          <div>
            <h1 className="font-display font-extrabold text-lg tracking-tight leading-none">Owner Console</h1>
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-0.5">Wayfinder Admin</p>
          </div>
        </div>
        <label className="font-mono text-xs uppercase tracking-widest text-moss">Email</label>
        <input data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="w-full mt-1 mb-3 px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm focus:outline-none focus:ring-2 focus:ring-terracotta" />
        <label className="font-mono text-xs uppercase tracking-widest text-moss">Password</label>
        <input data-testid="admin-password" type="password" value={password}
          onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
          className="w-full mt-1 mb-3 px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm focus:outline-none focus:ring-2 focus:ring-terracotta" />
        {err && <p className="text-sm text-destructive mb-3" data-testid="admin-error">{err}</p>}
        <button data-testid="admin-login-btn" onClick={submit} disabled={loading}
          className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 rounded-sm font-semibold tracking-wide transition-colors disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </button>
        <button onClick={() => navigate("/")} className="w-full mt-2 text-xs text-ink-muted hover:text-ink transition-colors flex items-center justify-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to site
        </button>
      </div>
    </div>
  );
}

const blank = (region) => ({
  region, name: "", type: "adventure", location_id: "", days: 1, difficulty: "Easy",
  fitness: "Low", elevation_max: 0, image: "", months: [...Array(12)].map((_, i) => i + 1),
  prices: { essential: 0, comfort: 0, signature: 0 }, description: "",
  highlights: [], inclusions: [], exclusions: [], details: {},
});

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-moss">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
const inputCls = "w-full px-3 py-2 text-sm bg-white border border-sand-dark rounded-sm text-ink focus:outline-none focus:ring-2 focus:ring-terracotta";

function ModuleEditor({ open, onClose, module, locations, region, onSaved }) {
  const isNew = !module?.id;
  const [f, setF] = useState(module);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(module); }, [module]);
  if (!f) return null;
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setPrice = (lvl, v) => setF((p) => ({ ...p, prices: { ...p.prices, [lvl]: v } }));
  const toggleMonth = (m) => setF((p) => ({ ...p, months: p.months.includes(m) ? p.months.filter((x) => x !== m) : [...p.months, m].sort((a, b) => a - b) }));
  const toArr = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    if (!f.location_id) { toast.error("Pick a location"); return; }
    setSaving(true);
    const body = {
      name: f.name, type: f.type, marker_type: f.type, location_id: f.location_id,
      days: Number(f.days), difficulty: f.difficulty, fitness: f.fitness,
      elevation_max: Number(f.elevation_max), image: f.image, gallery: f.image ? [f.image] : [],
      months: f.months, description: f.description,
      highlights: f.highlights, inclusions: f.inclusions, exclusions: f.exclusions,
      prices: { essential: Number(f.prices.essential), comfort: Number(f.prices.comfort), signature: Number(f.prices.signature) },
      details: { ...(f.details || {}), meals: f.details?.meals || "" },
    };
    try {
      const url = isNew ? `${API}/modules` : `${API}/modules/${module.id}`;
      const res = await fetch(url, {
        method: isNew ? "POST" : "PATCH",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify(isNew ? { ...body, region } : body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(typeof e.detail === "string" ? e.detail : "Save failed");
      }
      toast.success(isNew ? "Experience created" : "Experience updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e.message || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl rounded-md max-h-[92vh] overflow-y-auto thin-scroll" data-testid="module-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-xl tracking-tight">{isNew ? "New experience" : `Edit · ${module.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {f.image && <img src={f.image} alt="" className="w-full h-36 object-cover rounded-sm" />}
          <Field label="Name"><input data-testid="edit-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Type">
              <select data-testid="edit-type" className={inputCls} value={f.type} onChange={(e) => set("type", e.target.value)}>
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Location">
              <select data-testid="edit-location" className={inputCls} value={f.location_id} onChange={(e) => set("location_id", e.target.value)}>
                <option value="">— pick —</option>
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </Field>
            <Field label="Days"><input data-testid="edit-days" type="number" min={1} className={inputCls} value={f.days} onChange={(e) => set("days", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Difficulty">
              <select className={inputCls} value={f.difficulty} onChange={(e) => set("difficulty", e.target.value)}>
                {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Fitness">
              <select className={inputCls} value={f.fitness} onChange={(e) => set("fitness", e.target.value)}>
                {FITNESS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Max altitude (m)"><input type="number" className={inputCls} value={f.elevation_max} onChange={(e) => set("elevation_max", e.target.value)} /></Field>
          </div>
          <Field label="Feature image URL"><input data-testid="edit-image" className={inputCls} value={f.image} onChange={(e) => set("image", e.target.value)} placeholder="https://…" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Price · Essential"><input data-testid="edit-price-essential" type="number" className={inputCls} value={f.prices.essential} onChange={(e) => setPrice("essential", e.target.value)} /></Field>
            <Field label="Price · Comfort"><input data-testid="edit-price-comfort" type="number" className={inputCls} value={f.prices.comfort} onChange={(e) => setPrice("comfort", e.target.value)} /></Field>
            <Field label="Price · Signature"><input data-testid="edit-price-signature" type="number" className={inputCls} value={f.prices.signature} onChange={(e) => setPrice("signature", e.target.value)} /></Field>
          </div>
          <Field label="Available months">
            <div className="flex flex-wrap gap-1">
              {MONTHS.map((m, i) => (
                <button key={m} onClick={() => toggleMonth(i + 1)}
                  className={`font-mono text-[11px] px-2 py-1 rounded-sm ${f.months.includes(i + 1) ? "bg-moss text-white" : "bg-sand text-ink-muted"}`}>{m}</button>
              ))}
            </div>
          </Field>
          <Field label="Description"><textarea data-testid="edit-description" rows={3} className={inputCls} value={f.description} onChange={(e) => set("description", e.target.value)} /></Field>
          <Field label="Meals (traveller info)"><input className={inputCls} value={f.details?.meals || ""} onChange={(e) => set("details", { ...(f.details || {}), meals: e.target.value })} /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Highlights (one per line)"><textarea rows={4} className={inputCls} value={(f.highlights || []).join("\n")} onChange={(e) => set("highlights", toArr(e.target.value))} /></Field>
            <Field label="Included (one per line)"><textarea rows={4} className={inputCls} value={(f.inclusions || []).join("\n")} onChange={(e) => set("inclusions", toArr(e.target.value))} /></Field>
            <Field label="Excluded (one per line)"><textarea rows={4} className={inputCls} value={(f.exclusions || []).join("\n")} onChange={(e) => set("exclusions", toArr(e.target.value))} /></Field>
          </div>
          <button data-testid="editor-save" onClick={save} disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-11 rounded-sm font-semibold tracking-wide transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> {isNew ? "Create experience" : "Save changes"}</>}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AccessRequestsBell() {
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const unread = requests.filter((r) => !r.read).length;

  const load = () => fetch(`${API}/trade-access-requests`, { headers: authHeader() })
    .then((r) => r.ok ? r.json() : [])
    .then(setRequests).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const markRead = async (id) => {
    await fetch(`${API}/trade-access-requests/${id}/read`, { method: "PATCH", headers: authHeader() }).catch(() => {});
    setRequests((prev) => prev.map((r) => r.id === id ? { ...r, read: true } : r));
  };

  return (
    <div className="relative">
      <button data-testid="admin-bell" onClick={() => setOpen((o) => !o)}
        className="relative flex items-center justify-center w-10 h-10 rounded-sm border border-sand-dark hover:border-moss transition-colors">
        <Bell className="w-4 h-4 text-ink-secondary" />
        {unread > 0 && (
          <span data-testid="admin-bell-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-terracotta text-white text-[10px] font-bold rounded-full flex items-center justify-center">{unread}</span>
        )}
      </button>
      {open && (
        <div data-testid="admin-bell-dropdown" className="absolute right-0 mt-2 w-80 max-h-[70vh] overflow-y-auto thin-scroll bg-white border border-sand-dark rounded-md shadow-lg z-20">
          <div className="flex items-center justify-between px-4 py-3 border-b border-sand-dark sticky top-0 bg-white">
            <span className="font-display font-bold text-sm tracking-tight">Trade access requests</span>
            <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-ink-muted" /></button>
          </div>
          {requests.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-ink-muted">No requests yet.</p>
          ) : requests.map((r) => (
            <div key={r.id} data-testid={`access-request-${r.id}`}
              onClick={() => !r.read && markRead(r.id)}
              className={`px-4 py-3 border-b border-sand-dark cursor-pointer transition-colors ${r.read ? "bg-white" : "bg-moss-light/40 hover:bg-moss-light"}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{r.name}</span>
                {!r.read && <span className="w-2 h-2 rounded-full bg-terracotta" />}
              </div>
              {r.company && <p className="text-xs text-ink-secondary flex items-center gap-1 mt-0.5"><Building2 className="w-3 h-3" />{r.company}</p>}
              <p className="text-xs text-ink-secondary flex items-center gap-1 mt-0.5"><Mail className="w-3 h-3" />{r.email}</p>
              {r.phone && <p className="text-xs text-ink-secondary flex items-center gap-1 mt-0.5"><Phone className="w-3 h-3" />{r.phone}</p>}
              {r.message && <p className="text-xs text-ink-muted mt-1.5 leading-relaxed">{r.message}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BookingsInbox() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("enquiries");
  useEffect(() => {
    fetch(`${API}/admin/bookings`, { headers: authHeader() })
      .then((r) => r.json()).then(setD).catch(() => setD({ enquiries: [], reservations: [], call_requests: [] }));
  }, []);
  if (!d) return <div className="flex items-center text-ink-secondary"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading…</div>;
  const tabs = [
    ["enquiries", "Enquiries", d.enquiries],
    ["reservations", "Reservations", d.reservations],
    ["call_requests", "Call requests", d.call_requests],
  ];
  const rows = { enquiries: d.enquiries, reservations: d.reservations, call_requests: d.call_requests }[tab];
  return (
    <div data-testid="admin-bookings">
      <div className="flex gap-2 mb-5">
        {tabs.map(([k, label, list]) => (
          <button key={k} data-testid={`bookings-tab-${k}`} onClick={() => setTab(k)}
            className={`px-3.5 py-2 rounded-sm text-sm font-medium border transition-colors ${tab === k ? "bg-ink text-white border-ink" : "bg-white border-sand-dark text-ink-secondary hover:border-moss"}`}>
            {label} <span className="font-mono text-xs opacity-70">({list.length})</span>
          </button>
        ))}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-muted py-10 text-center border border-dashed border-sand-dark rounded-md">Nothing here yet.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={r.id || i} data-testid={`booking-row-${tab}-${i}`} className="border border-sand-dark rounded-sm p-4 bg-white">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-display font-bold text-sm">
                    {tab === "call_requests" ? r.name : `${r.first_name || ""} ${r.last_name || ""}`.trim()}
                    {r.email && <span className="font-normal text-ink-secondary"> · {r.email}</span>}
                  </p>
                  <p className="font-mono text-[11px] text-ink-muted mt-0.5">
                    {(r.phone_code || "") + " " + (r.phone || "")}
                    {r.created_at && ` · ${new Date(r.created_at).toLocaleString()}`}
                  </p>
                </div>
                {tab === "reservations" && (
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${r.deposit_option === "now" ? "bg-terracotta text-white" : "bg-moss-light text-moss"}`}>
                    {r.deposit_option === "now" ? "Pay now" : `Due ${r.deposit_due || "7d"}`}
                  </span>
                )}
              </div>
              <div className="mt-2 text-sm text-ink-secondary space-y-0.5">
                {tab === "enquiries" && <>
                  <p><b>{r.destination}</b>{r.tour_slug ? ` · ${r.tour_slug}` : ""}</p>
                  <p className="font-mono text-[11px] text-ink-muted">{[r.month, r.year, r.duration, r.travellers && `${r.travellers} pax`, r.budget].filter(Boolean).join(" · ")}</p>
                  {r.comments && <p className="italic">"{r.comments}"</p>}
                  {r.bikes && <p className="text-sm text-ink-secondary" data-testid="booking-bikes">Bikes: {r.bikes}</p>}
                  {r.heard_about && <p className="font-mono text-[11px] text-ink-muted">Heard via: {r.heard_about}{r.newsletter ? " · newsletter ✓" : ""}</p>}
                </>}
                {tab === "reservations" && <p><b>{r.trip_name}</b> · ${r.deposit_amount} deposit{r.tour_slug ? ` · ${r.tour_slug}` : ""}</p>}
                {tab === "call_requests" && <>
                  <p>{r.trip_name || "(general)"}</p>
                  {r.preferred_times?.length > 0 && <p className="font-mono text-[11px] text-ink-muted">Prefers: {r.preferred_times.join(" / ")}</p>}
                  {r.notes && <p className="italic">"{r.notes}"</p>}
                </>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(isAdmin());
  const [regions, setRegions] = useState([]);
  const [region, setRegion] = useState("nepal");
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("experiences");
  const navigate = useNavigate();

  useEffect(() => {
    if (!authed) return;
    fetchMe().catch(() => { clearToken(); setAuthed(false); });
    getRegions().then(setRegions).catch(() => {});
  }, [authed]);

  const loadRegion = () => getRegion(region).then(setData).catch(() => {});
  useEffect(() => { if (authed) loadRegion(); /* eslint-disable-next-line */ }, [authed, region]);

  const del = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/modules/${m.id}`, { method: "DELETE", headers: authHeader() });
      if (!res.ok) throw new Error();
      toast.success("Experience deleted");
      loadRegion();
    } catch (_) { toast.error("Delete failed"); }
  };

  if (!authed) return <LoginScreen onDone={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-base" data-testid="admin-page">
      <header className="flex items-center justify-between px-6 h-16 border-b border-sand-dark bg-white sticky top-0 z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-terracotta rounded-sm flex items-center justify-center"><Compass className="w-5 h-5 text-white" /></div>
          <div>
            <h1 className="font-display font-extrabold text-lg tracking-tight leading-none">Owner Console</h1>
            <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mt-0.5">Experience Library — full control</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-sm border border-sand-dark overflow-hidden mr-1">
            <button data-testid="admin-view-experiences" onClick={() => setView("experiences")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "experiences" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Experiences</button>
            <button data-testid="admin-view-bookings" onClick={() => setView("bookings")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "bookings" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Bookings</button>
            <button data-testid="admin-view-trips" onClick={() => setView("trips")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "trips" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Trip Groups</button>
            <button data-testid="admin-view-partners" onClick={() => setView("partners")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "partners" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Partners</button>
            <button data-testid="admin-view-campaigns" onClick={() => setView("campaigns")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "campaigns" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Campaigns</button>
            <button data-testid="admin-view-commissions" onClick={() => setView("commissions")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "commissions" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Commissions</button>
            <button data-testid="admin-view-currency" onClick={() => setView("currency")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "currency" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Currency</button>
            <button data-testid="admin-view-media" onClick={() => setView("media")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "media" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Media</button>
            <button data-testid="admin-view-recent" onClick={() => setView("recent")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "recent" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Recent Video</button>
            <button data-testid="admin-view-wetravel" onClick={() => setView("wetravel")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "wetravel" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Payments</button>
            <button data-testid="admin-view-callingcard" onClick={() => setView("callingcard")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "callingcard" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Calling Card</button>
            <button data-testid="admin-view-videoreviews" onClick={() => setView("videoreviews")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "videoreviews" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Video Reviews</button>
            <button data-testid="admin-view-images" onClick={() => setView("images")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "images" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Site Images</button>
            <button data-testid="admin-view-voice" onClick={() => setView("voice")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${view === "voice" ? "bg-moss text-white" : "bg-white text-ink-secondary hover:bg-sand"}`}>Raj's Voice</button>
          </div>
          <AccessRequestsBell />
          <button data-testid="admin-open-builder" onClick={() => navigate(`/build/${region}`)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm border border-sand-dark text-sm font-medium hover:border-moss transition-colors">
            <MapPin className="w-4 h-4" /> Videos on map
          </button>
          <button data-testid="admin-logout" onClick={() => { clearToken(); setAuthed(false); }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-sm bg-ink text-white text-sm font-medium hover:opacity-90 transition-opacity">
            <LogOut className="w-4 h-4" /> Log out
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto p-6">
        {view === "bookings" ? <BookingsInbox /> : view === "trips" ? <TripGroupsAdmin /> : view === "partners" ? <MPNPartnersAdmin /> : view === "campaigns" ? <CampaignCentreAdmin /> : view === "commissions" ? <CommissionsAdmin /> : view === "currency" ? <CurrencyAdmin /> : view === "media" ? <TourMediaAdmin /> : view === "recent" ? <RecentAdventureAdmin /> : view === "wetravel" ? <WeTravelAdmin /> : view === "callingcard" ? <CallingCardAdmin /> : view === "videoreviews" ? <VideoReviewsAdmin /> : view === "images" ? <MediaRecoveryAdmin /> : view === "voice" ? <VoiceAdmin /> : (
        <>
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {regions.map((r) => (
            <button key={r.id} data-testid={`admin-region-${r.id}`} onClick={() => setRegion(r.id)}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${region === r.id ? "bg-moss text-white border-moss" : "bg-white border-sand-dark text-ink-secondary hover:border-moss"}`}>
              {r.name}
            </button>
          ))}
          <button data-testid="admin-add-experience" onClick={() => setEditing(blank(region))}
            className="ml-auto flex items-center gap-1.5 bg-terracotta hover:bg-terracotta-hover text-white px-3.5 py-2 rounded-sm text-sm font-semibold transition-colors">
            <Plus className="w-4 h-4" /> Add experience
          </button>
        </div>

        {!data ? (
          <div className="flex items-center text-ink-secondary"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading…</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {data.modules.map((m) => (
              <div key={m.id} data-testid={`admin-module-${m.id}`} className="flex gap-3 border border-sand-dark rounded-sm p-3 bg-white">
                <img src={m.image} alt="" className="w-20 h-20 rounded-sm object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-sm tracking-tight truncate">{m.name}</p>
                  <p className="font-mono text-[11px] text-ink-muted">{m.type} · {m.days}d · {m.difficulty}</p>
                  <p className="font-mono text-xs mt-1">${m.prices.essential} / ${m.prices.comfort} / ${m.prices.signature}</p>
                  <div className="flex gap-1.5 mt-2">
                    <button data-testid={`admin-edit-${m.id}`} onClick={() => setEditing(m)}
                      className="flex items-center gap-1 bg-moss hover:bg-moss-hover text-white px-2.5 py-1 rounded-sm text-xs font-semibold transition-colors">
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                    <button data-testid={`admin-delete-${m.id}`} onClick={() => del(m)}
                      className="flex items-center gap-1 border border-sand-dark text-destructive px-2.5 py-1 rounded-sm text-xs font-semibold hover:bg-terracotta-light transition-colors">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </div>

      <ModuleEditor
        open={!!editing} onClose={() => setEditing(null)} module={editing}
        locations={data?.locations || []} region={region} onSaved={loadRegion}
      />
    </div>
  );
}
